import React, { useCallback, useEffect, useState, useMemo } from 'react';
import Head from '../../../layout/head/Head';
import Content from '../../../layout/content/Content';
import {
  Block,
  BlockDes,
  BlockHeadContent,
  BlockTitle,
  BlockBetween,
  BlockHead,
  Icon,
  DataTable,
} from '../../../components/Component';
import {
  newRoles,
  LeadStatusUncheckedReturn,
} from '../../../utils/envConfig';
import { toast } from 'react-toastify';
import 'react-phone-number-input/style.css';
import { useSelector, useDispatch } from 'react-redux';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import { fetchAgents, fetchUsers } from '../../../redux/actions/user';
import useMutationQuery from '../../../hooks/useMutationQuery';
import { returnLeads } from '../../../api/property-cards/returnLeads';
import { useQueryClient } from 'react-query';
import { showToast } from '../../../utils/toast/toast';
import { fetchGlobalSettings } from '../../../redux/actions/settings';
import LeadsListFilterBar from './leadsListTable/LeadsListFilterBar';
import ActiveFiltersDisplay from './leadsListTable/ActiveFiltersDisplay';
import LeadsListBulkActions from './leadsListTable/LeadsListBulkActions';
import LeadsDataTableHeaderTools from './leadsListTable/LeadsDataTableHeaderTools';
import LeadsDataTable from './leadsListTable/LeadsDataTable';
import LeadsListPagination from './leadsListTable/LeadsListPagination';
import LeadEditModal from './leadsListTable/LeadEditModal';
import LeadDetailsModal from './leadsListTable/LeadDetailsModal';
import ReturnLeadConfirmationModal from './leadsListTable/ReturnLeadConfirmationModal';
import BulkReturnConfirmationModal from './leadsListTable/BulkReturnConfirmationModal';
import useLeadsListFilters from './leadsListTable/hooks/useLeadsListFilters';
import useLeadsData from './leadsListTable/hooks/useLeadsData';
import useLeadsTableInteractions from './leadsListTable/hooks/useLeadsTableInteractions';
import useLeadsModals from './leadsListTable/hooks/useLeadsModals';

const LeadsListTable = ({ title, initialData = [], name, role = null, hide = false, viewMode = "default" }) => {
  const axios = useAxiosPrivate();

  const authuser = useSelector((state) => state?.user?.loggedInUser);

  // State Variables
  const [tablesm, updateTableSm] = useState(false);
  const [onSearch, setonSearch] = useState(true); // For toggling search bar visibility in header tools
  const [actionText, setActionText] = useState(''); // For bulk actions

  const handleBulkActionChangeWrapper = (value) => {
    setActionText(value);
  };
  const dispatch = useDispatch();
  const [bulkActionAgent, setBulkActionAgent] = useState(null); // For bulk action agent RSelect
  const [saveLoading, setSaveLoading] = useState(false); // For bulk action save
  const [reAssignmentReason, setReAssignmentReason] = useState(''); // For bulk action
  const [selectedLeadSource, setSelectedLeadSource] = useState(null); // For bulk action

  const [optAgentsLocal, setOptAgentsLocal] = useState([]);

  const interactions = useLeadsTableInteractions();

  const {
    filterStatus,
    filterRole,
    selectedOptions,
    filterLeadSource,
    selectedFilterTags,
    selectedChannelPartners,
    rangeStart,
    rangeEnd,
    filterAgent,
    selectedAgent,
    newLeadsSort,
    search,
    filterSearch,
    filterLeadStatus,
    filterLeadLabel,
    temporaryLabel,
    allTags,
    optLeadSource,
    handleStatusOption,
    handleRoleOption,
    handleLeadSource,
    handleLeadLabelOption,
    handleTagsChange,
    handleChannelPartnersChange,
    handleAgentChange,
    handleRangeStartChange,
    handleRangeEndChange,
    handleNewLeadsSort,
    handleHeaderSearchSubmit,
    handleLeadStatusOption,
    clearAllFiltersHandler,
    handleBadgeRemoveAndLeadSource,
    clearBadge,
  } = useLeadsListFilters(axios, role, interactions.setCurrentPage, optAgentsLocal, viewMode);


  const allowedRoles = ['User', 'Owner', 'Investor'];
  const queryClient = useQueryClient();

  useEffect(() => {
    dispatch(fetchGlobalSettings(axios));
  }, [dispatch, axios]);

  const returnMutation = useMutationQuery((payload) => returnLeads(axios, payload), {
    mutationKey: 'returnLeads',
    onSettled: () => {
      queryClient.invalidateQueries('leadsWithPropCards');
      queryClient.invalidateQueries('authAgentData');
    },
    onError: (error) => {
      console.error(error);
      showToast(error?.message ?? 'Returning leads failed', 'error');
    },
    onSuccess: (data) => {
      if (data && data?.length >= 0) {
        showToast(`Successfully returned ${data?.length} lead(s).`, 'success');
      } else {
        showToast('Successfully returned leads.', 'success');
      }
    }
  });

  useEffect(() => {
    dispatch(fetchAgents(axios));
  }, [axios, dispatch]);

  const agentRedux = useSelector((state) => state.user?.agents);
  useEffect(() => {
    let options = [];
    if (agentRedux && agentRedux.length > 0) {
      agentRedux
        .filter((agents) => agents?.user?.active === true)
        .forEach((a) =>
          options.push({
            value: a?.id,
            label: a?.user?.first_name + ' ' + a?.user?.last_name
          })
        );
    }
    setOptAgentsLocal(options);
  }, [agentRedux]);

  const bulkActionOptions = [
    { value: 'assign', label: 'Assign to agent', roles: [newRoles.Admin, newRoles.MasterAdmin, newRoles.ChannelPartnerManager] },
    { value: 'return', label: 'Return Leads', roles: [newRoles.Agent, newRoles.AnonymousAgent, newRoles.TeamLeader] }
  ];

  const filteredOptions = bulkActionOptions.filter((option) =>
    option.roles.some((allowedRole) => authuser?.roles?.includes(allowedRole))
  );

  const validate = (agentIdValue) => {
    if (!agentIdValue || agentIdValue === 'Select') {
      toast.error('Select Agent');
      return false;
    } else if (!reAssignmentReason.trim()) {
      toast.error('Please provide a reason for reassignment.');
      return false;
    } else {
      return true;
    }
  };
  const onActionClick = async (e) => {
    const selectedIdsReassign = interactions.selectedItems.map((item) => item.id);
    e.preventDefault();
    if (validate(bulkActionAgent?.value)) {
      const formData = {
        agent_id: bulkActionAgent?.value,
        user_ids: selectedIdsReassign,
        notes: reAssignmentReason,
        leadSource: selectedLeadSource?.id,
        leadSourceName: selectedLeadSource?.name
      };

      try {
        setSaveLoading(true);
        await axios.post('/usersAgents/assign', formData, { headers });
        toast.success('Agent assigned successfully!');
      } catch (err) {
        if (err.response && err.response.data && err.response.data.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Users not added to Agent!');
        }
      } finally {
        setSaveLoading(false);
        interactions.setSelectedItems([]);
        setActionText(''); // Reset bulk action type
        setBulkActionAgent(null); // Reset selected agent for bulk action
        setReAssignmentReason(''); // Reset reassignment reason
        setSelectedLeadSource(null); // Reset selected lead source for bulk action
        if (axios && buildLinkFromHook && dispatch && fetchUsers) {
          dispatch(fetchUsers(axios, buildLinkFromHook)); // Re-dispatch fetchUsers
        }
      }
    }
  };

  const {
    users: usersFromHook,
    isLoading,
    filCount,
    buildLink: buildLinkFromHook,
  } = useLeadsData(axios, {
    filterSearch,
    filterRole,
    initialRole: role,
    filterStatus,
    filterLeadSource,
    filterLeadLabel,
    selectedFilterTags,
    selectedChannelPartners,
    rangeStart,
    rangeEnd,
    filterAgent,
    filterLeadStatus,
    newLeadsSort,
    currentPage: interactions.currentPage,
    itemsPerPage: interactions.itemsPerPage,
    sortBy: interactions.sortBy,
    sort: interactions.sort,
    allowedRoles,
    viewMode,
  });

  useEffect(() => {
    if (usersFromHook && interactions.setFetchedUsers) {
      interactions.setFetchedUsers(usersFromHook);
    }
  }, [usersFromHook, interactions.setFetchedUsers]);

  const onEditSuccessCallback = useCallback(() => {
    queryClient.invalidateQueries('leadsWithPropCards');
    setActionText('');
    interactions.setSelectedItems([]);
    if (axios && buildLinkFromHook && dispatch && fetchUsers) {
      dispatch(fetchUsers(axios, buildLinkFromHook));
    }
  }, [queryClient, setActionText, interactions, axios, buildLinkFromHook, dispatch]);

  const {
    view,
    user: modalUser,
    formData: modalFormData,
    returnModalState,
    isBulkReturnConfirmOpen,
    statusChecked,
    toggle: toggleModal,
    onFormCancel: onModalFormCancel,
    loadDetail: loadModalDetail,
    handleEditModal: handleModalEdit,
    toggleReturnConfirm: toggleModalReturnConfirm,
    toggleBulkReturnConfirm: toggleModalBulkReturnConfirm,
    handleStatusChange: handleModalStatusChange,
    handleEditSubmitSuccess: handleModalEditSubmitSuccess,
    handleSingleReturnConfirm: handleModalSingleReturnConfirm,
    handleBulkReturnConfirm: handleModalBulkReturnConfirm,
    setStatusChecked: setModalStatusChecked,
  } = useLeadsModals(axios, onEditSuccessCallback, returnMutation);

  const { access_token } = useSelector((state) => state?.user?.loggedInUser) || '';
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${access_token}`
  }), [access_token]);

  const CustomMultiValueLabel = ({ data, ...props }) => (
    <div className="selected-option" {...props}>
      {data.label}
    </div>
  );

  const [smOption, setSmOption] = useState(false);

  useEffect(() => {
    const initialStatusChecked = interactions.selectedItems.reduce((acc, item) => {
      if (item.status) {
        acc[item.status] = !LeadStatusUncheckedReturn.includes(item.status);
      }
      return acc;
    }, {});
    if (setModalStatusChecked) {
      setModalStatusChecked(prev => ({ ...prev, ...initialStatusChecked }));
    }
  }, [interactions.selectedItems, setModalStatusChecked]);

  const toggleSearch = () => setonSearch(!onSearch); // For toggling search bar visibility in header tools

  const handleViewLeadDetails = (itemId, item) => {
    loadModalDetail(itemId, item);
    toggleModal('details');
  };

  return (
    <React.Fragment>
      <Head title={title}></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                {title}
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>
                  You have total {filCount} {name + 's'}.
                </p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="toggle-wrap nk-block-tools-toggle">
                <a
                  href="#more"
                  className="btn btn-icon btn-trigger toggle-expand me-n1"
                  onClick={(ev) => {
                    ev.preventDefault();
                    setSmOption(!smOption);
                  }}
                >
                  <Icon name="more-v" />
                </a>
                <div className={`toggle-expand-content ${smOption ? "expanded" : ""}`} style={{ display: smOption ? 'block' : 'none' }}>
                  <ul className="nk-block-tools g-3">
                  </ul>
                </div>
              </div>
            </BlockHeadContent>
          </BlockBetween>
          <LeadsListFilterBar
            authuser={authuser}
            newLeadsSort={newLeadsSort}
            onNewLeadsSortToggle={handleNewLeadsSort}
            filterStatus={filterStatus}
            onStatusChange={handleStatusOption}
            filterRole={filterRole}
            onRoleChange={handleRoleOption}
            allowedRoles={allowedRoles}
            temporaryLabel={temporaryLabel}
            filterLeadLabel={filterLeadLabel}
            onLabelChange={handleLeadLabelOption}
            optLeadSource={optLeadSource}
            selectedLeadSourceOptions={selectedOptions}
            onLeadSourceChange={handleLeadSource}
            CustomMultiValueLabel={CustomMultiValueLabel}
            allTags={allTags}
            selectedFilterTags={selectedFilterTags}
            onTagsChange={handleTagsChange}
            selectedChannelPartners={selectedChannelPartners}
            onChannelPartnersChange={handleChannelPartnersChange}
            currentFilterLeadStatus={filterLeadStatus}
            onLeadStatusChange={handleLeadStatusOption}
            optAgents={optAgentsLocal}
            selectedAgent={selectedAgent}
            onAgentChange={handleAgentChange}
            rangeStart={rangeStart}
            onRangeStartChange={handleRangeStartChange}
            rangeEnd={rangeEnd}
            onRangeEndChange={handleRangeEndChange}
            viewMode={viewMode}
          />
        </BlockHead>

        <ActiveFiltersDisplay
          filterSearch={filterSearch}
          filterLeadStatus={filterLeadStatus}
          filterRole={filterRole}
          selectedLeadSourceOptions={selectedOptions}
          selectedAgent={selectedAgent}
          filterLeadLabel={filterLeadLabel}
          selectedFilterTags={selectedFilterTags}
          selectedChannelPartners={selectedChannelPartners}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          onClearSearchFilter={() => handleHeaderSearchSubmit(null)}
          onClearLeadStatusFilter={() => handleLeadStatusOption(null)}
          onClearRoleFilter={() => handleRoleOption(null)}
          onClearLeadSourceOption={handleBadgeRemoveAndLeadSource}
          onClearAgentFilter={() => handleAgentChange(null)}
          onClearLabelFilter={() => handleLeadLabelOption(null)}
          onClearTagFilter={(tagToRemove) => {
            const newTags = selectedFilterTags.filter(t => t.value !== tagToRemove.value);
            handleTagsChange(newTags);
          }}
          onClearChannelPartnerFilter={(partnerToRemove) => {
            const newPartners = selectedChannelPartners.filter(p => p.value !== partnerToRemove.value);
            handleChannelPartnersChange(newPartners);
          }}
          onClearDateFilter={() => {
            handleRangeStartChange(null);
            handleRangeEndChange(null);
          }}
          onClearAllFilters={clearAllFiltersHandler}
          showClearAll={clearBadge}
        />
        <Block>
          <LeadsListBulkActions
            selectedItemsCount={interactions.selectedItems.length}
            authuser={authuser}
            agentOptions={optAgentsLocal}
            bulkActionOptions={filteredOptions}
            currentBulkAction={actionText}
            onBulkActionChange={handleBulkActionChangeWrapper}
            selectedAgentForBulk={bulkActionAgent}
            onAgentChangeForBulk={setBulkActionAgent}
            selectedLeadSourceForBulk={selectedLeadSource}
            onLeadSourceChangeForBulk={setSelectedLeadSource}
            reAssignmentReasonForBulk={reAssignmentReason}
            onReAssignmentReasonChangeForBulk={setReAssignmentReason}
            onBulkAssign={onActionClick}
            onTriggerBulkReturn={toggleModalBulkReturnConfirm}
            saveLoading={saveLoading}
          />
        </Block>
        <Block>
          <DataTable className="card-stretch">
            <LeadsDataTableHeaderTools
              itemsPerPage={interactions.itemsPerPage}
              sortDirection={interactions.sort}
              initialSearchTerm={search}
              isSearchVisible={!onSearch}
              isCompactView={tablesm}
              onItemsPerPageChange={interactions.handleItemsPerPageChange}
              onSortDirectionChange={(dir) => interactions.handleSortColumn(interactions.sortBy)}
              onSearchSubmit={handleHeaderSearchSubmit}
              onToggleSearchVisibility={toggleSearch}
              onToggleCompactView={updateTableSm}
            />
            <LeadsDataTable
              leads={interactions.displayUsers}
              isLoading={isLoading}
              selectedItemIds={interactions.selectedItems}
              currentSortBy={interactions.sortBy}
              currentSortDir={interactions.sort}
              authuser={authuser}
              checkedAll={interactions.checkedAll}
              isCompactView={tablesm}
              onSelectRow={interactions.onSelectChange}
              onSelectAllRows={interactions.selectorCheck}
              onSortColumn={interactions.handleSortColumn}
              onViewDetails={handleViewLeadDetails}
              onEditLead={handleModalEdit}
              onReturnLead={toggleModalReturnConfirm}
            />
            <LeadsListPagination
              itemPerPage={interactions.itemsPerPage}
              totalItems={filCount}
              paginate={interactions.paginate}
              currentPage={interactions.currentPage}
              isLoading={isLoading}
              dataLength={interactions.displayUsers?.length}
            />
          </DataTable>
        </Block>

        <LeadDetailsModal
          isOpen={view.details}
          leadData={modalUser}
          toggle={onModalFormCancel}
        />

        <LeadEditModal
          isOpen={view.edit}
          leadData={modalFormData}
          axiosInstance={axios}
          toggle={onModalFormCancel}
          onSubmitSuccess={handleModalEditSubmitSuccess}
        />

        <ReturnLeadConfirmationModal
          isOpen={returnModalState.isModalOpen}
          toggle={() => toggleModalReturnConfirm()}
          onConfirm={() => handleModalSingleReturnConfirm()}
          isLoading={returnMutation.isLoading}
        />

        <BulkReturnConfirmationModal
          isOpen={isBulkReturnConfirmOpen}
          toggle={toggleModalBulkReturnConfirm}
          onConfirmBulkReturn={() => handleModalBulkReturnConfirm(interactions.selectedItems)}
          isLoading={returnMutation.isLoading}
          selectedItemsForReturn={interactions.selectedItems}
          statusChecked={statusChecked}
          onStatusChange={handleModalStatusChange}
        />

      </Content>
    </React.Fragment>
  );
};

export default LeadsListTable;
